import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axiosInstance';

export const CANVASS_API = '/purchase/canvasses';

/**
 * Opens a canvass covering every line of a request.
 *
 * Canvassing is per request, not per material: requests here average 120 lines,
 * and the same suppliers are asked for the whole list at once.
 */
export const useCanvassFromRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (purchaseRequestId: string) => {
      const { data } = await axiosInstance.post(
        `${CANVASS_API}/from-request/${purchaseRequestId}`,
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
};

/** Records one supplier's answer on one item. */
export const useSaveQuote = (canvassId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      lineId: string;
      itemId: string;
      available?: boolean | null;
      quotedPrice?: number;
      leadTimeDays?: number;
      remarks?: string;
    }) => {
      const { data } = await axiosInstance.put(
        `${CANVASS_API}/${canvassId}/quote`,
        body,
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
};

/**
 * Sends the comparison for a decision.
 *
 * Recorded as the submission, which is what puts it in an approver's queue and
 * what the counter-signature rule is checked against: whoever submits cannot
 * also award.
 */
export const useSubmitCanvass = (canvassId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { remarks?: string }) => {
      const { data } = await axiosInstance.put(
        `${CANVASS_API}/${canvassId}/submit`,
        body,
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
};

/**
 * Awards the canvass. With an itemId it applies to that item alone; without
 * one it applies to everything the supplier can carry.
 */
export const useAward = (canvassId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      lineId?: string;
      itemId?: string;
      remarks?: string;
    }) => {
      const { data } = await axiosInstance.put(
        `${CANVASS_API}/${canvassId}/award`,
        body,
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
};

/** Raises one order per supplier holding awarded items. */
export const useGeneratePurchaseOrders = (canvassId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.post(
        `${CANVASS_API}/${canvassId}/generate-po`,
      );
      return data as { id?: string; code?: string }[];
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
};

/**
 * Edits one material on the canvass, chiefly to supply a missing quantity.
 *
 * Quantities arrive copied from the request, and for materials costed per set
 * in PDMops -- plaster especially -- that figure is often zero because the cost
 * datasheet was never filled in. This lets purchasing establish it while
 * sourcing instead of going back upstream first.
 */
export const useSaveItem = (canvassId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      itemId: string;
      quantity?: number;
      description?: string;
      uomId?: string;
    }) => {
      const { data } = await axiosInstance.put(
        `${CANVASS_API}/${canvassId}/item`,
        body,
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
};
